import { Component, OnInit, ElementRef, QueryList, ViewChildren } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { MenuProductService } from '../../services/menu-product/menu-product.service';
import { EditableProductListComponent } from '../../ui/molecules/editable-product-list/editable-product-list.component';
import { MenuProduct, MenuProductGroup } from '../../models/menu-product.model';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { KeycloakService } from 'keycloak-angular';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ProductItemModalComponent } from '../../components/products/modal/product-item-modal/product-item-modal.component';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';
import { CategoryService } from '../../services/categories/categories.service';
import { CategoryDialogComponent } from '../categories-admin/category-dialog.component';
import { PortionsService } from '../../services/portions/portions.service';

@Component({
  selector: 'app-menu-products',
  standalone: true,
  imports: [CommonModule, FormsModule, EditableProductListComponent, MatDialogModule, MatButtonModule, MatIconModule, MatSlideToggleModule, MatProgressSpinnerModule],
  templateUrl: './menu-products.component.html',
  styleUrls: ['./menu-products.component.css']
})
export class MenuProductsComponent implements OnInit {
  groups: MenuProductGroup[] = [];
  isLoading = true;
  error: string | null = null;
  displayedColumns: string[] = ['name', 'description', 'price', 'actions'];
  // Track open categories for accordion behavior
  openCategories = new Set<number>();
  // RBAC flag to control editing actions
  isAdmin: boolean = false;
  // Refs to headers for scrolling into view
  @ViewChildren('catHeader') private catHeaders!: QueryList<ElementRef<HTMLElement>>;
  // Track category enabled state and updating status
  private categoriesEnabled = new Map<string, boolean>();
  private updatingCategories = new Set<string>();
  private renamingCategories = new Set<string>();

  constructor(
    private menuProductService: MenuProductService,
    private dialog: MatDialog,
    private keycloak: KeycloakService,
    private categoriesSvc: CategoryService,
    private portionsSvc: PortionsService,
  ) {}

  ngOnInit(): void {
    this.loadProducts();
    // Determine admin privileges from Keycloak roles
    try {
      const rolesA = (this.keycloak?.getUserRoles ? this.keycloak.getUserRoles() : []) as string[];
      const rolesB = (this.keycloak?.getUserRoles ? this.keycloak.getUserRoles(true) : []) as string[];
      const all = [...new Set([...(rolesA || []), ...(rolesB || [])].map(r => (r || '').toLowerCase()))];
      let has = all.includes('admin');
      const inst: any = (this.keycloak as any)?.getKeycloakInstance?.();
      if (inst?.hasRealmRole && inst.hasRealmRole('admin')) has = true;
      if (inst?.hasResourceRole && inst.hasResourceRole('admin')) has = true;
      this.isAdmin = !!has;
      console.log('[MenuProducts] Roles detectados:', { rolesA, rolesB, isAdmin: this.isAdmin });
    } catch {
      this.isAdmin = false;
    }
  }

  // Accordion helpers
  isOpen(index: number): boolean {
    return this.openCategories.has(index);
  }

  toggleCategory(index: number): void {
    const willOpen = !this.openCategories.has(index);
    if (this.openCategories.has(index)) {
      this.openCategories.delete(index);
    } else {
      // Open only this one and close others
      this.openCategories.clear();
      this.openCategories.add(index);
    }
    if (willOpen) {
      setTimeout(() => this.scrollCategoryIntoView(index), 0);
    }
  }

  private scrollCategoryIntoView(index: number): void {
    try {
      const el = this.catHeaders?.toArray()?.[index]?.nativeElement;
      if (!el) return;
      const headerOffset = 90;
      const rect = el.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
      const top = rect.top + scrollTop - headerOffset;
      window.scrollTo({ top, behavior: 'smooth' });
    } catch {}
  }

  // Create a copy of a product within the same category
  onDuplicateProduct(source: MenuProduct, group?: MenuProductGroup): void {
    if (!source) { return; }
    // Prefer UUID fields
    const categoryUuid = (source.categoryUuid || (source as any).categoryId || (source as any).categoryHash || source.category?.uuid || (group as any)?.categoryId || '') as string;
    const categoryName = (source.categoryName || source.category?.name || (group as any)?.categoryName || '') as string;
    const unitTypeUuid = ((source as any).unitTypeUuid || (source as any).portionUuid || (source as any).unitTypeId || (source as any).portionHash || '') as string;
    // Send explicit portionUuid when available (backend prefers portion linkage on copy)
    const portionUuid = ((source as any).portionUuid || (source as any).unitTypeUuid || (source as any).unitTypeId || (source as any).portionHash || '') as string;

    const payload: Omit<MenuProduct, 'id'> = {
      name: `${source.name} (copia)`,
      description: source.description,
      sellPrice: Number((source as any).sellPrice ?? 0),
      costPrice: Number((source as any).costPrice ?? 1),
      isActive: source.isActive ?? true,
      categoryUuid,
      categoryName,
      // keep same unit type when available
      unitTypeUuid,
      // and include portionUuid for correct portion association
      portionUuid,
      sku: undefined,
      createdAt: undefined,
      updatedAt: undefined,
    };

    this.menuProductService.createMenuProduct(payload).subscribe({
      next: () => {
        this.loadProducts();
      },
      error: (err) => {
        console.error('Error duplicating product:', err);
        this.error = 'Error al duplicar el producto. Por favor, intente de nuevo.';
      }
    });
  }

  loadProducts(): void {
    this.isLoading = true;
    this.error = null;
    
    this.menuProductService.getMenuProductsGroupedByCategory()
      .pipe(
        finalize(() => this.isLoading = false)
      )
      .subscribe({
        next: (groups) => {
          this.groups = groups;
          // Open first category by default when none is open
          if (Array.isArray(this.groups) && this.groups.length > 0 && this.openCategories.size === 0) {
            this.openCategories.add(0);
          }
          // Fetch category enabled states
          this.categoriesSvc.getCategories().subscribe({
            next: (cats: any[]) => {
              const map = new Map<string, boolean>();
              (cats || []).forEach((c: any) => {
                const id = String(c?.uuid || c?.id || c?.hash || '');
                if (id) map.set(id, !!c?.enabled);
              });
              this.categoriesEnabled = map;
            },
            error: (err) => {
              console.warn('No se pudieron cargar estados de categorías:', err);
            }
          });
        },
        error: (err) => {
          console.error('Error loading products:', err);
          this.error = 'Error al cargar los productos. Por favor, intente de nuevo.';
        }
      });
  }

  onUpdateProduct(updatedProduct: MenuProduct): void {
    this.isLoading = true;
    // Normalize payload to always include UUID fields when available
    const m: any = { ...updatedProduct };
    // portionUuid should be set from unitTypeUuid/Id or existing portionUuid
    m.portionUuid = m.portionUuid || m.unitTypeUuid || m.unitTypeId || undefined;
    if (!m.categoryUuid) {
      m.categoryUuid = m.categoryId || m.categoryHash || m.category?.uuid || undefined;
    }
    // Ensure costPrice is always provided: use existing product's cost if available; otherwise default to 1
    if (m.costPrice === undefined || m.costPrice === null) {
      const existing = (this.groups || [])
        .flatMap((g: any) => g.items || [])
        .find((p: any) => (p?.uuid && p.uuid === (m.uuid || m.id)) || (p?.id && p.id === m.id));
      m.costPrice = existing?.costPrice ?? 1;
    }
    // Coerce numbers
    if (m.costPrice !== undefined) m.costPrice = Number(m.costPrice);
    if (m.sellPrice !== undefined) m.sellPrice = Number(m.sellPrice);
    // Clean id/hash aliases if present
    delete m.portionHash;
    delete m.categoryId;
    delete m.categoryHash;
    this.menuProductService.updateProduct(m).subscribe({
      next: () => {
        // Always reload full catalog to reflect changes such as category/portion updates
        this.loadProducts();
      },
      error: () => {
        this.error = 'Error al actualizar el producto. Por favor, intente de nuevo.';
        this.loadProducts();
      }
    });
  }

  onDeleteProduct(productId: string): void {
    // La confirmación se realiza en el componente hijo (EditableProductListComponent).
    // Aquí sólo ejecutamos la eliminación y recargamos el catálogo al finalizar.
    this.menuProductService.deleteProduct(productId).subscribe({
      next: () => {
        // Refrescar siempre el catálogo para mantener consistencia con el backend
        this.loadProducts();
      },
      error: (err) => {
        console.error('Error deleting product:', err);
        this.error = 'Error al eliminar el producto. Por favor, intente de nuevo.';
        this.loadProducts();
      }
    });
  }

  openCreateProduct(): void {
    const ref = this.dialog.open(ProductItemModalComponent, {
      width: '720px',
      data: null,
    });
    ref.afterClosed().subscribe(() => {
      // Refresh the catalog after closing (in case a new product was created)
      this.loadProducts();
    });
  }

  openCreateProductForCategory(group: MenuProductGroup): void {
    if (!group) return;
    const baseProduct: any = {
      categoryUuid: group.categoryId,
      categoryId: group.categoryId,
      categoryName: group.categoryName,
    };
    const ref = this.dialog.open(ProductItemModalComponent, {
      width: '720px',
      data: { product: baseProduct },
    });
    ref.afterClosed().subscribe(() => {
      this.loadProducts();
    });
  }

  // Edit category name using existing atomic modal
  onRenameCategory(group: MenuProductGroup): void {
    if (!group) return;

    // Cargar categoría completa (para obtener uuid y porciones actuales)
    this.categoriesSvc.getCategories().subscribe({
      next: (cats: any[]) => {
        const cat = (cats || []).find((c: any) => String(c?.id) === String(group.categoryId) || String(c?.uuid) === String(group.categoryId));
        const categoryUuid = String(cat?.uuid || group.categoryId || '');
        const currentPortions: any[] = (cat?.portions || cat?.categoryPortions || []);
        const currentPortionUuids: string[] = (currentPortions || []).map((p: any) => String(p?.uuid || p?.id || '')).filter(Boolean);

        // Cargar todas las porciones disponibles para permitir asociarlas
        this.portionsSvc.getPortions().subscribe({
          next: (all: any[]) => {
            const portionOptions = (all || []).map((p: any) => ({ uuid: String(p?.uuid || p?.id), name: String(p?.name || '') }));

            const ref = this.dialog.open(CategoryDialogComponent, {
              data: {
                title: 'Renombrar categoría',
                actionLabel: 'Guardar',
                name: group.categoryName,
                slug: undefined,
                categoryUuid,
                portionOptions,
                selectedPortionUuids: currentPortionUuids,
              }
            });

            ref.afterClosed().subscribe(payload => {
              if (!payload || !payload.name) return;
              const id = group.categoryId;
              this.renamingCategories.add(String(id));

              // Actualizar nombre de categoría
              this.categoriesSvc.updateCategory(id, { name: payload.name }).pipe(finalize(() => {
                this.renamingCategories.delete(String(id));
              })).subscribe({
                next: () => {
                  // Si el modal devuelve porciones, sincronizar vínculos
                  const updatedUuids: string[] = Array.isArray(payload.portionUuids) ? payload.portionUuids : currentPortionUuids;
                  const prevSet = new Set(currentPortionUuids);
                  const nextSet = new Set(updatedUuids);

                  const toLink: string[] = [];
                  const toUnlink: string[] = [];

                  nextSet.forEach(u => { if (!prevSet.has(u)) toLink.push(u); });
                  prevSet.forEach(u => { if (!nextSet.has(u)) toUnlink.push(u); });

                  // Enlazar nuevas porciones
                  toLink.forEach(portionUuid => {
                    if (!portionUuid || !categoryUuid) return;
                    this.categoriesSvc.linkPortionToCategories({ portionUuid, categoryUuids: [categoryUuid] }).subscribe({
                      error: (err) => console.error('Error linking portion to category', err),
                    });
                  });

                  // Desasociar porciones removidas
                  toUnlink.forEach(portionUuid => {
                    if (!portionUuid || !categoryUuid) return;
                    this.categoriesSvc.unlinkPortionFromCategory({ portionUuid, categoryUuid }).subscribe({
                      error: (err) => console.error('Error unlinking portion from category', err),
                    });
                  });

                  this.loadProducts();
                },
                error: (err) => {
                  console.error('Rename category failed', err);
                  this.error = 'No se pudo renombrar la categoría.';
                }
              });
            });
          },
          error: (err) => {
            console.error('Load portions failed', err);
            this.error = 'No se pudieron cargar las porciones.';
          }
        });
      },
      error: (err) => {
        console.error('Load categories failed', err);
        this.error = 'No se pudo cargar la categoría.';
      }
    });
  }

  // Delete category only when it has no menu-products
  onDeleteCategory(group: MenuProductGroup): void {
    if (!group) return;
    const hasProducts = (group.items?.length || 0) > 0;
    if (hasProducts) return; // safety guard; button is hidden in this case
    const ref = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Eliminar categoría',
        message: `¿Eliminar la categoría "${group.categoryName}"? Esta acción no se puede deshacer.`,
        cancelLabel: 'Cancelar',
        confirmLabel: 'Eliminar',
        confirmColor: 'warn'
      }
    });
    ref.afterClosed().subscribe(ok => {
      if (!ok) return;
      const id = group.categoryId;
      this.categoriesSvc.deleteCategory(id).subscribe({
        next: () => {
          this.loadProducts();
        },
        error: (err) => {
          console.error('Delete category failed', err);
          this.error = 'No se pudo eliminar la categoría.';
          this.loadProducts();
        }
      });
    });
  }

  // Helpers for header controls
  isCategoryEnabled(id: string): boolean {
    return this.categoriesEnabled.get(String(id)) ?? true;
  }

  isCategoryUpdating(id: string): boolean {
    return this.updatingCategories.has(String(id));
  }

  isCategoryRenaming(id: string): boolean {
    return this.renamingCategories.has(String(id));
  }

  onToggleCategoryEnabled(group: MenuProductGroup, checked: boolean): void {
    if (!group) return;
    const id = group.categoryId;
    this.updatingCategories.add(String(id));
    this.categoriesSvc.updateCategory(id, { enabled: checked }).subscribe({
      next: () => {
        this.categoriesEnabled.set(String(id), !!checked);
        // Opcionalmente recargar para reflejar cambios globales
        // this.loadProducts();
      },
      error: (err) => {
        console.error('Toggle categoría falló', err);
        this.error = 'No se pudo actualizar la visibilidad de la categoría.';
      },
      complete: () => {
        this.updatingCategories.delete(String(id));
      }
    });
  }
}
